import React, { useState, useEffect, useRef } from 'react';
import { TextField, IconButton, Box, Typography, CircularProgress, Tabs, Tab, Button } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import ChatIcon from '@mui/icons-material/Chat';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import { getStoredOrganization } from '../../utils/organizationSession';
import { useAnonymousAuth } from '../../hooks/useAnonymousAuth';

interface AnonymousChatInterfaceProps {
  orgToken?: string;
}

const AnonymousChatInterface: React.FC<AnonymousChatInterfaceProps> = ({ orgToken }) => {
  const [newMessage, setNewMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [messages, setMessages] = useState<Array<{ text: string; isBot: boolean; timestamp: string }>>([]);
  const [error, setError] = useState<string | null>(null);
  const [organizationInfo, setOrganizationInfo] = useState<{ token?: string; organizationId?: string }>({});
  const { project, requestWithAutoRefresh } = useAnonymousAuth();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Navigation state: 'chat' or 'hub'
  const [activeTab, setActiveTab] = useState<'chat' | 'hub'>('chat');
  const RESOURCE_HUB_URL =
    (process.env.REACT_APP_RESOURCE_HUB_URL as string | undefined) ||
    (process.env.RESROUCE_HUB_URL as string | undefined) ||
    '';

  useEffect(() => {
    // Get organization info from URL token or localStorage
    if (orgToken) {
      setOrganizationInfo({ token: orgToken });
    } else {
      // No URL token, check localStorage
      const storedOrg = getStoredOrganization();
      if (storedOrg) {
        setOrganizationInfo({ 
          token: storedOrg.token,
          organizationId: storedOrg.organization_id 
        });
      }
    }
  }, [orgToken]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'auto' });
    }
  }, [messages]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const text = newMessage.trim();
    if (!text || isLoading) return;

    setNewMessage('');
    setIsLoading(true);
    setError(null);

    const userMsg = { text, isBot: false, timestamp: new Date().toISOString() };
    setMessages((prev) => [...prev, userMsg]);

    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
      const url = `${API_BASE_URL}/bedrock/retrieve`;
      const res = await requestWithAutoRefresh(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query_text: text }),
      });

      if (!res.ok) {
        const t = await res.text().catch(() => '');
        throw new Error(t || `${res.status} ${res.statusText}`);
      }
      const data = await res.json().catch(() => ({}));
      const extractAssistantText = (payload: any): string => {
        try {
          const content = payload?.content;
          if (Array.isArray(content)) {
            const combined = content
              .filter((c: any) => c && typeof c.text === 'string')
              .map((c: any) => c.text)
              .join('\n\n');
            if (combined) return combined;
          }
          if (typeof payload?.text === 'string') return payload.text;
        } catch (_) {}
        return '';
      };
      const assistantText = extractAssistantText(data) || '[no content]';
      const botMsg = { text: assistantText, isBot: true, timestamp: new Date().toISOString() };
      setMessages((prev) => [...prev, botMsg]);
    } catch (err: any) {
      console.error('Anonymous retrieve failed:', err);
      setError(err?.message || 'Failed to send message');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100dvh',
        width: '100%',
        backgroundColor: '#fff',
        overflow: 'hidden',
      }}
    >
      {/* Navigation Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          padding: '8px 8px',
          borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
          backgroundColor: '#fff',
          minHeight: '56px',
        }}
      >
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          aria-label="Anonymous Chat navigation"
          textColor="primary"
          indicatorColor="primary"
        >
          <Tab
            value="chat"
            icon={<ChatIcon fontSize="small" />}
            iconPosition="start"
            label="Help Chat"
          />
          <Tab
            value="hub"
            icon={<MenuBookIcon fontSize="small" />}
            iconPosition="start"
            label="Resource Hub"
          />
        </Tabs>

      </Box>

      {/* Main Content Area */}
      <Box
        sx={{
          flexGrow: 1,
          overflow: 'auto',
          padding: activeTab === 'chat' ? '16px 20px' : 0,
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          backgroundColor: activeTab === 'chat' ? '#f8f9fa' : '#fff',
        }}
      >
        {activeTab === 'hub' ? (
          RESOURCE_HUB_URL ? (
            <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <MenuBookIcon color="primary" />
              <Typography variant="h6">Resource Hub</Typography>
              <Button
                variant="contained"
                color="primary"
                href={RESOURCE_HUB_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                Open Resource Hub
              </Button>
            </Box>
          ) : (
            <Box sx={{ p: 3 }}>
              <Typography color="error" variant="body2">
                Resource Hub URL is not configured. Please set REACT_APP_RESOURCE_HUB_URL in your environment.
              </Typography>
            </Box>
          )
        ) : messages.length === 0 ? (
          <>
            {/* Empty State */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                textAlign: 'center',
                color: '#666',
                minHeight: '200px',
              }}
            >
              <ChatIcon sx={{ fontSize: 48, mb: 2, color: '#999' }} />
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 400 }}>
                Ask a question
              </Typography>
              <Typography variant="body2" sx={{ color: '#999', maxWidth: '280px' }}>
                Type a message below to begin chatting
              </Typography>
            </Box>

            {/* Loading State */}
            {isLoading && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  padding: '16px',
                }}
              >
                <CircularProgress size={24} />
              </Box>
            )}
          </>
        ) : (
          <>
            {/* Messages List */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {messages.map((m, idx) => (
                <Box key={idx} sx={{ alignSelf: m.isBot ? 'flex-start' : 'flex-end', maxWidth: '85%' }}>
                  <Box
                    sx={{
                      bgcolor: m.isBot ? '#f0f0f0' : 'primary.main',
                      color: m.isBot ? 'text.primary' : 'primary.contrastText',
                      borderRadius: '12px',
                      px: 2,
                      py: 1.5,
                    }}
                  >
                    <Typography variant="caption" sx={{ opacity: 0.7, display: 'block', mb: 0.5 }}>
                      {m.isBot ? (project?.name || 'Assistant') : 'You'}
                    </Typography>
                    <Typography sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {m.text}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>

            {/* Inline loading indicator while waiting for assistant */}
            {isLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                <CircularProgress size={20} />
              </Box>
            )}

            {/* Inline error */}
            {error && (
              <Typography variant="caption" color="error">
                {error}
              </Typography>
            )}
          </>
        )}
        {/* Bottom anchor to ensure view scrolls to last message */}
        {activeTab === 'chat' && <Box ref={messagesEndRef} sx={{ height: 0 }} />}
      </Box>

      {/* Input Container (only for AI Chat) */}
      {activeTab === 'chat' && (
        <Box
          sx={{
            padding: '16px 20px',
            borderTop: '1px solid rgba(0, 0, 0, 0.12)',
            backgroundColor: '#fff',
          }}
        >
          <form onSubmit={handleSubmit}>
            <Box
              sx={{
                display: 'flex',
                gap: '12px',
                alignItems: 'flex-end',
              }}
            >
              <TextField
                multiline
                maxRows={4}
                placeholder="Type your message..."
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSubmit(e);
                  }
                }}
                disabled={isLoading}
                variant="outlined"
                size="small"
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '20px',
                    backgroundColor: '#f8f9fa',
                    '&:hover': {
                      backgroundColor: '#f3f4f6',
                    },
                    '&.Mui-focused': {
                      backgroundColor: '#fff',
                    },
                  },
                  '& .MuiOutlinedInput-multiline': {
                    padding: '12px 16px',
                  },
                }}
              />
              <IconButton
                type="submit"
                disabled={!newMessage.trim() || isLoading}
                color="primary"
                sx={{
                  padding: '10px',
                  backgroundColor: 'primary.main',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  },
                  '&:disabled': {
                    backgroundColor: 'rgba(0, 0, 0, 0.12)',
                    color: 'rgba(0, 0, 0, 0.26)',
                  },
                }}
              >
                <SendIcon />
              </IconButton>
            </Box>
          </form>
        </Box>
      )}
    </Box>
  );
};

export default AnonymousChatInterface;
